// src/features/auth/token-management/storage/CookieStorage.ts

export interface CookieOptions {
  expires?: Date | number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
  maxAge?: number;
}

export interface CookieAttributes extends CookieOptions {
  name: string;
  value: string;
}

export class CookieStorage {
  private defaultOptions: CookieOptions;
  private isServer: boolean;

  constructor(defaultOptions: CookieOptions = {}) {
    this.defaultOptions = {
      path: '/',
      secure: true,
      sameSite: 'strict',
      ...defaultOptions,
    };
    
    this.isServer = typeof window === 'undefined';
  }

  /**
   * Set a cookie
   */
  set(name: string, value: string, options: CookieOptions = {}): void {
    if (this.isServer) {
      console.warn('CookieStorage: Cannot set cookies on server side');
      return;
    }

    const opts = { ...this.defaultOptions, ...options };
    const cookieString = this.serialize(name, value, opts);
    
    document.cookie = cookieString;
  }

  /**
   * Get a cookie value
   */
  get(name: string): string | null {
    if (this.isServer) {
      return null;
    }

    const cookies = this.parse(document.cookie);
    return cookies[name] || null;
  }

  /**
   * Get all cookies
   */
  getAll(): Record<string, string> {
    if (this.isServer) {
      return {};
    }

    return this.parse(document.cookie);
  }

  /**
   * Remove a cookie
   */
  remove(name: string, options: CookieOptions = {}): void {
    const opts = { ...this.defaultOptions, ...options };
    
    // Set cookie with past expiration date
    this.set(name, '', {
      ...opts,
      expires: new Date(0),
      maxAge: 0,
    });
  }

  /**
   * Check if a cookie exists
   */
  has(name: string): boolean {
    return this.get(name) !== null;
  }

  /**
   * Clear all cookies for the current path/domain
   */
  clear(options: CookieOptions = {}): void {
    const cookies = this.getAll();
    
    Object.keys(cookies).forEach(name => {
      this.remove(name, options);
    });
  }

  /**
   * Parse cookie string into object
   */
  private parse(cookieString: string): Record<string, string> {
    const cookies: Record<string, string> = {};
    
    if (!cookieString) {
      return cookies;
    }

    cookieString.split(';').forEach(cookie => {
      const [name, ...valueParts] = cookie.trim().split('=');
      if (name) {
        const value = valueParts.join('=');
        cookies[name] = this.decode(value);
      }
    });

    return cookies;
  }

  /**
   * Serialize cookie attributes into cookie string
   */
  private serialize(name: string, value: string, options: CookieOptions): string {
    const encodedValue = this.encode(value);
    let cookieString = `${name}=${encodedValue}`;

    // Add expires
    if (options.expires) {
      const expires = options.expires instanceof Date 
        ? options.expires 
        : new Date(Date.now() + options.expires);
      cookieString += `; Expires=${expires.toUTCString()}`;
    }

    // Add max-age
    if (options.maxAge !== undefined) {
      cookieString += `; Max-Age=${options.maxAge}`;
    }

    // Add domain
    if (options.domain) {
      cookieString += `; Domain=${options.domain}`;
    }

    // Add path
    if (options.path) {
      cookieString += `; Path=${options.path}`;
    }

    // Add secure flag
    if (options.secure) {
      cookieString += '; Secure';
    }

    // Add httpOnly flag (note: this won't work from JavaScript)
    if (options.httpOnly) {
      cookieString += '; HttpOnly';
    }

    // Add sameSite
    if (options.sameSite) {
      cookieString += `; SameSite=${this.capitalizeSameSite(options.sameSite)}`;
    }

    return cookieString;
  }

  /**
   * Encode cookie value
   */
  private encode(value: string): string {
    return encodeURIComponent(value);
  }

  /**
   * Decode cookie value
   */
  private decode(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  /**
   * Capitalize SameSite value
   */
  private capitalizeSameSite(sameSite: string): string {
    return sameSite.charAt(0).toUpperCase() + sameSite.slice(1).toLowerCase();
  }

  /**
   * Get cookie metadata
   */
  getMetadata(name: string): {
    exists: boolean;
    value?: string;
    size?: number;
  } {
    const value = this.get(name);
    
    if (!value) {
      return { exists: false };
    }

    return {
      exists: true,
      value,
      size: new Blob([value]).size,
    };
  }

  /**
   * Set JSON value in cookie
   */
  setJSON<T>(name: string, value: T, options: CookieOptions = {}): void {
    try {
      const jsonString = JSON.stringify(value);
      this.set(name, jsonString, options);
    } catch (error) {
      console.error('Failed to serialize JSON for cookie:', error);
    }
  }

  /**
   * Get JSON value from cookie
   */
  getJSON<T>(name: string): T | null {
    const value = this.get(name);
    
    if (!value) {
      return null;
    }

    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  /**
   * Set secure cookie with encryption (requires crypto implementation)
   */
  async setSecure(
    name: string, 
    value: string, 
    key: string,
    options: CookieOptions = {}
  ): Promise<void> {
    // This would require a crypto implementation
    // For now, just set with httpOnly and secure flags
    this.set(name, value, {
      ...options,
      secure: true,
      httpOnly: true,
      sameSite: 'strict',
    });
  }

  /**
   * Get secure cookie with decryption
   */
  async getSecure(name: string): Promise<string | null> {
    // This would require a crypto implementation
    return this.get(name);
  }

  /**
   * Monitor cookie changes
   */
  watch(
    name: string, 
    callback: (value: string | null, oldValue: string | null) => void,
    interval: number = 1000
  ): () => void {
    let currentValue = this.get(name);
    
    const timer = setInterval(() => {
      const newValue = this.get(name);
      
      if (newValue !== currentValue) {
        callback(newValue, currentValue);
        currentValue = newValue;
      }
    }, interval);

    // Return cleanup function
    return () => clearInterval(timer);
  }

  /**
   * Get cookie size in bytes
   */
  getSize(name: string): number {
    const value = this.get(name);
    if (!value) return 0;
    
    // Calculate size including name and value
    return new Blob([`${name}=${value}`]).size;
  }

  /**
   * Get total size of all cookies
   */
  getTotalSize(): number {
    const cookies = this.getAll();
    let totalSize = 0;
    
    Object.entries(cookies).forEach(([name, value]) => {
      totalSize += new Blob([`${name}=${value}`]).size;
    });

    return totalSize;
  }

  /**
   * Check if cookies are enabled
   */
  static isEnabled(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const testKey = '__cookie_test__';
      document.cookie = `${testKey}=1`;
      const enabled = document.cookie.includes(testKey);
      
      if (enabled) {
        // Clean up test cookie
        document.cookie = `${testKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
      }
      
      return enabled;
    } catch {
      return false;
    }
  }

  /**
   * Get cookie limitations
   */
  static getLimitations(): {
    maxSize: number;
    maxCookies: number;
    maxNameLength: number;
    maxValueLength: number;
  } {
    return {
      maxSize: 4096, // 4KB per cookie
      maxCookies: 50, // Approximate browser limit
      maxNameLength: 1024,
      maxValueLength: 3072,
    };
  }
}